import React, { useEffect } from 'react';
import styled from 'styled-components';
import ReactModal from 'react-modal';
import { theme } from '../../styles/theme';
import Button from './Button';

// Make sure to set the app element for accessibility
if (typeof window !== 'undefined') {
  ReactModal.setAppElement('#root');
}

// Add custom styles for the ReactModal overlay
const customModalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 9990, // Very high z-index for the overlay
  }
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  showCloseButton = true,
  closeOnOverlayClick = true,
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
   
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
 
  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={closeOnOverlayClick ? onClose : undefined}
      shouldCloseOnOverlayClick={closeOnOverlayClick}
      contentLabel={title}
      size={size}
      style={customModalStyles}
    >
      <ModalHeader>
        <Title>{title}</Title>
        {showCloseButton && (
          <CloseButton onClick={onClose} aria-label="Close modal">
            ✕
          </CloseButton>
        )}
      </ModalHeader>
      <ModalContent>
        {children}
      </ModalContent>
    </StyledModal>
  );
};

// PdfModal is a specialized modal for displaying PDFs
export const PdfModal = ({ isOpen, onClose, pdfPath }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Dokument Ansicht"
      size="large"
    >
      <PdfContainer>
        {pdfPath ? (
          <PdfIframe  
            src={`/api/view-pdf?path=${encodeURIComponent(pdfPath)}`}
            title="PDF Viewer"
            allowFullScreen
          />
        ) : (
          <div>Keine PDF-Datei verfügbar</div>
        )}
      </PdfContainer>
      <ButtonContainer>
        <Button onClick={onClose}>Schließen</Button>
      </ButtonContainer>
    </Modal>
  );
};

const getModalWidth = (size) => {
  switch (size) {
    case 'small':
      return '400px';
    case 'large':
      return '900px';
    case 'xlarge':
      return '1200px';
    case 'medium':
    default:
      return '600px';
  }
};

// Updated StyledModal component with much higher z-index for the modal and overlay
const StyledModal = styled(ReactModal)`
  position: relative;
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.boxShadow.xl};
  padding: 0;
  max-width: ${({ size }) => getModalWidth(size)};
  width: 95%;
  max-height: 90vh;
  outline: none;
  margin: 2rem auto;
  display: flex;
  flex-direction: column;
 
  /* Center modal on screen */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999; /* Significantly increased z-index for the modal */
 
  /* Scroll for content if needed */
  overflow: hidden;
 
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 95%;
    max-height: 95vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${theme.colors.border};
`;

const Title = styled.h2`
  font-size: 1.25rem;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  color: ${theme.colors.textLight};
  transition: color ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
 
  &:hover {
    color: ${theme.colors.text};
  }
`;

const ModalContent = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
`;

const PdfContainer = styled.div`
  width: 100%;
  height: 70vh;
  overflow: hidden;
  padding: 0;
  margin-bottom: 1rem;
`;

const PdfIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

export default Modal;